<template>
  <div class="tokens-view">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        variant="text"
        class="mr-2"
        :to="{ name: 'developer' }"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4">{{ t('dev.myTokens') }}</h1>
      
      <v-spacer></v-spacer>
      
      <v-btn
        color="primary"
        prepend-icon="mdi-plus"
        @click="dialog = true"
      >
        Criar Token
      </v-btn>
    </div>
    
    <v-card class="mb-6">
      <v-card-text>
        <div class="text-body-1 mb-2">
          Aqui você pode gerenciar todos os tokens QRC20 que você criou na rede QuarnX. Crie novos tokens, acompanhe sua distribuição e gerencie suas configurações.
        </div>
        <v-alert
          color="info"
          icon="mdi-information-outline"
          variant="tonal"
          density="compact"
        >
          Tokens QRC20 são o padrão nativo da QuarnX, permitindo a criação de ativos digitais fungíveis com recursos avançados de segurança.
        </v-alert>
      </v-card-text>
    </v-card>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
      class="mt-4"
    ></v-skeleton-loader>
    
    <template v-else>
      <v-card v-if="tokens.length === 0" class="pa-4 text-center">
        <v-icon size="64" color="grey" class="mb-3">mdi-currency-usd-off</v-icon>
        <div class="text-h6 mb-2">{{ t('dev.noTokens') }}</div>
        <div class="text-body-2 mb-4">
          Comece criando seu primeiro token QRC20 para usar em seu aplicativo descentralizado.
        </div>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="dialog = true"
        >
          Criar Meu Primeiro Token
        </v-btn>
      </v-card>
      
      <div v-else>
        <v-row>
          <v-col 
            v-for="token in tokens" 
            :key="token.address"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card height="100%">
              <v-card-title class="d-flex align-center">
                <v-avatar color="primary" class="mr-2">
                  <span class="text-h6 text-uppercase">{{ token.symbol.charAt(0) }}</span>
                </v-avatar>
                {{ token.name }}
                <v-chip
                  v-if="token.verified"
                  size="small"
                  color="success"
                  class="ml-2"
                >
                  Verificado
                </v-chip>
              </v-card-title>
              
              <v-card-text>
                <div class="d-flex mb-2">
                  <v-chip size="small" color="primary" variant="outlined" class="mr-2">
                    {{ token.symbol }}
                  </v-chip>
                  <div class="text-caption grey--text d-flex align-center">
                    <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                    {{ formatarData(token.createdAt) }}
                  </div>
                </div>
                
                <div class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.tokenAddress') }}</div>
                  <div class="d-flex align-center">
                    <span class="text-truncate">{{ token.address }}</span>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      class="ml-1"
                      @click="copiarEndereco(token.address)"
                    >
                      <v-icon size="small">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </div>
                
                <div class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.totalSupply') }}</div>
                  <div class="font-weight-medium">
                    {{ formatarNumero(token.totalSupply) }} {{ token.symbol }}
                  </div>
                </div>
                
                <div class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.holders') }}</div>
                  <div class="font-weight-medium">
                    {{ formatarNumero(token.holders) }} endereços
                  </div>
                </div>
                
                <div class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('tx.fee') }}</div>
                  <div class="text-caption text-grey mb-4">
                    Taxa: 0.3% ({{ (valorCompra * 0.003).toFixed(5) }} {{ coin }})
                  </div>
                </div>
                
                <div class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.transfers24h') }}</div>
                  <div class="font-weight-medium">
                    {{ formatarNumero(token.transfers24h) }} transações
                  </div>
                </div>

                <!-- QRC-21 v2: Protocol Fee Status -->
                <div class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.protocolFee') }}</div>
                  <div class="d-flex align-center">
                    <v-chip
                      :color="token.protocol_fee_enabled ? 'success' : 'grey'"
                      size="x-small"
                      class="mr-2"
                    >
                      {{ token.protocol_fee_enabled ? 'Habilitada' : 'Desabilitada' }}
                    </v-chip>
                    <span v-if="token.protocol_fee_enabled" class="text-caption">
                      {{ (token.protocol_fee_rate || 0) }} bp ({{ ((token.protocol_fee_rate || 0) / 100).toFixed(2) }}%)
                    </span>
                  </div>
                </div>

                <div v-if="token.protocol_fee_enabled" class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.totalFees') }}</div>
                  <div class="font-weight-medium">
                    {{ formatarNanoKinX(token.total_fees_generated) }}
                  </div>
                </div>

                <!-- QRC-21 v2: Metadata URI -->
                <div v-if="token.metadata_uri" class="mb-2">
                  <div class="text-caption text-grey mb-1">{{ t('dev.metadataUri') }}</div>
                  <div class="d-flex align-center">
                    <a
                      :href="resolverIPFS(token.metadata_uri)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary text-decoration-none text-truncate"
                      style="max-width: 200px;"
                    >
                      {{ token.metadata_uri }}
                    </a>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      class="ml-1"
                      @click="copiarEndereco(token.metadata_uri)"
                    >
                      <v-icon size="small">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Metadata resolvida do IPFS -->
                <div v-if="token._resolvedMetadata" class="mb-2">
                  <div class="d-flex align-center mb-1">
                    <v-avatar
                      v-if="token._resolvedMetadata.logo"
                      size="24"
                      class="mr-2"
                    >
                      <v-img :src="resolverIPFS(token._resolvedMetadata.logo)" />
                    </v-avatar>
                    <span class="text-caption font-weight-medium">
                      {{ token._resolvedMetadata.description || '' }}
                    </span>
                  </div>
                  <div v-if="token._resolvedMetadata.website" class="text-caption">
                    <a :href="token._resolvedMetadata.website" target="_blank" rel="noopener noreferrer" class="text-primary text-decoration-none">
                      {{ token._resolvedMetadata.website }}
                    </a>
                  </div>
                </div>
              </v-card-text>
              
              <v-divider></v-divider>
              
              <v-card-actions>
                <v-btn 
                  variant="text" 
                  color="primary"
                  :to="{ name: 'account-details', params: { address: token.address } }"
                >
                  Ver Detalhes
                </v-btn>
                <v-spacer></v-spacer>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      v-bind="props"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      @click="abrirTransferencia(token)"
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi-send</v-icon>
                      </template>
                      <v-list-item-title>{{ t('dev.transfer') }}</v-list-item-title>
                    </v-list-item>
                    
                    <v-list-item
                      @click="abrirVerificacao(token)"
                      v-if="!token.verified"
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi-check-decagram</v-icon>
                      </template>
                      <v-list-item-title>{{ t('dev.verifyContract') }}</v-list-item-title>
                    </v-list-item>
                    
                    <v-list-item
                      @click="abrirEdicao(token)"
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi-pencil</v-icon>
                      </template>
                      <v-list-item-subtitle>Total em {{ coin }} movimentado hoje</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </template>
    
    <!-- Diálogo para criar novo token -->
    <v-dialog
      v-model="dialog"
      max-width="600"
    >
      <v-card>
        <v-card-title class="text-h5">
          Criar Novo Token QRC20
        </v-card-title>
        
        <v-card-text>
          <v-form ref="form" @submit.prevent="criarToken">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="novoToken.name"
                    label="Nome do Token*"
                    required
                    hint="Nome completo do seu token, ex: 'QuarnX Gold'"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="novoToken.symbol"
                    label="Símbolo*"
                    required
                    hint="Abreviação de 3-5 letras, ex: 'QXG'"
                    counter="5"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="novoToken.decimals"
                    label="Casas Decimais"
                    type="number"
                    hint="Normalmente 18 decimais"
                    :rules="[v => (v >= 0 && v <= 18) || 'Deve estar entre 0 e 18']"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12">
                  <v-text-field
                    v-model="novoToken.totalSupply"
                    label="Suprimento Total*"
                    type="number"
                    required
                    hint="Quantidade total de tokens que serão criados"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12">
                  <v-textarea
                    v-model="novoToken.description"
                    :label="t('gov.description')"
                    hint="Descreva o propósito do seu token"
                    rows="3"
                  ></v-textarea>
                </v-col>
                
                <v-col cols="12">
                  <v-checkbox
                    v-model="novoToken.burnable"
                    label="Pode ser queimado (destruído)"
                  ></v-checkbox>
                  
                  <v-checkbox
                    v-model="novoToken.mintable"
                    label="Pode ser minerado (criar novos)"
                  ></v-checkbox>
                  
                  <v-checkbox
                    v-model="novoToken.pausable"
                    label="Pode ser pausado (suspender transferências)"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="dialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="criarToken"
            :loading="enviando"
          >
            Criar Token
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { useDeveloperStore } from '@/stores/developer'
import { formatarData, formatarNumero } from '@/services/utilidades'

export default {
  name: 'DeveloperTokensView',
  data() {
    return {
      carregando: true,
      tokens: [],
      dialog: false,
      enviando: false,
      novoToken: {
        name: '',
        symbol: '',
        decimals: 18,
        totalSupply: 1000000,
        description: '',
        burnable: false,
        mintable: false,
        pausable: false
      },
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 3000
      },
      enderecoDeployer: ''
    }
  },
  computed: {
    developerStore() {
      return useDeveloperStore()
    }
  },
  methods: {
    formatarData,
    formatarNumero,

    /**
     * Converte nanoKinX para KinX formatado.
     * @param {number} nanoKinX - Valor em nanoKinX
     * @returns {string} Valor formatado em KinX
     */
    formatarNanoKinX(nanoKinX) {
      const coinSymbol = window.__QUARNX_COIN__ || 'tKinX'
      if (!nanoKinX || nanoKinX === 0) return `0 ${coinSymbol}`
      const kinx = nanoKinX / 1_000_000_000
      return `${kinx.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 9 })} ${coinSymbol}`
    },

    /**
     * Resolve um URI IPFS para um gateway HTTP público.
     * @param {string} uri - URI no formato ipfs://...
     * @returns {string} URL HTTP acessível
     */
    resolverIPFS(uri) {
      if (!uri) return ''
      if (uri.startsWith('ipfs://')) {
        return `https://ipfs.io/ipfs/${uri.slice(7)}`
      }
      return uri
    },
    
    async carregarTokens() {
      this.carregando = true
      
      try {
        await this.developerStore.fetchTokens()
        this.tokens = (this.developerStore.tokens || []).map(t => ({ ...t }))

        // Resolve IPFS metadata for tokens that have metadata_uri
        this.tokens.forEach(async (token, idx) => {
          if (token.metadata_uri) {
            try {
              const url = this.resolverIPFS(token.metadata_uri)
              const resp = await fetch(url, { signal: AbortSignal.timeout(5000) })
              if (resp.ok) {
                const meta = await resp.json()
                this.tokens[idx] = { ...this.tokens[idx], _resolvedMetadata: meta }
                this.tokens = [...this.tokens]
              }
            } catch {
              // IPFS resolution failed — silently ignore
            }
          }
        })
      } catch (error) {
        console.error('Erro ao carregar tokens:', error)
        this.mostrarSnackbar('Erro ao carregar tokens', 'error')
      } finally {
        this.carregando = false
      }
    },
    
    copiarEndereco(endereco) {
      navigator.clipboard.writeText(endereco)
        .then(() => {
          this.mostrarSnackbar('Endereço copiado para a área de transferência')
        })
        .catch(err => {
          console.error('Erro ao copiar endereço:', err)
        })
    },
    
    async criarToken() {
      if (!this.novoToken.name || !this.novoToken.symbol || !this.novoToken.totalSupply) {
        this.mostrarSnackbar('Preencha todos os campos obrigatórios', 'error')
        return
      }
      
      this.enviando = true
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        // Compile the token contract
        const compileResult = await ExplorerAPI.compilador.compilar(
          this.novoToken.codigo || `contract ${this.novoToken.name} {}`,
          this.novoToken.name
        )
        if (compileResult.status === 'compiled' && compileResult.wasm_base64) {
          // Deploy the compiled WASM
          const deployResult = await ExplorerAPI.contratos.deploy(
            compileResult.wasm_base64, 'wasm', this.enderecoDeployer
          )
          if (deployResult.status === 'deployed') {
            const novoToken = {
              ...this.novoToken,
              address: deployResult.contract_address,
              createdAt: new Date(),
              verified: false,
              holders: 1,
              transfers24h: 0,
              status: 'deployed'
            }
            
            await this.developerStore.addToken(novoToken)
            this.tokens.push(novoToken)
            
            this.mostrarSnackbar('Token criado e deployado com sucesso')
            this.dialog = false
            this.resetarFormulario()
          } else {
            this.mostrarSnackbar(`Erro no deploy: ${deployResult.status || deployResult.error}`, 'error')
          }
        } else {
          this.mostrarSnackbar(`Erro na compilação: ${compileResult.errors || 'Falha desconhecida'}`, 'error')
        }
      } catch (error) {
        console.error('Erro ao criar token:', error)
        this.mostrarSnackbar(`Erro: ${error.message}`, 'error')
      } finally {
        this.enviando = false
      }
    },
    
    resetarFormulario() {
      this.novoToken = {
        name: '',
        symbol: '',
        decimals: 18,
        totalSupply: 1000000,
        description: '',
        burnable: false,
        mintable: false,
        pausable: false
      }
    },
    
    mostrarSnackbar(texto, cor = 'success') {
      this.snackbar.text = texto
      this.snackbar.color = cor
      this.snackbar.show = true
    },
    
    abrirTransferencia(token) {
      // Em uma versão futura, implementar modal de transferência
      this.mostrarSnackbar(`Transferência de ${token.symbol} será implementada em breve`, 'info')
    },
    
    abrirVerificacao(token) {
      // Em uma versão futura, implementar verificação de contrato
      this.mostrarSnackbar(`Verificação de ${token.symbol} será implementada em breve`, 'info')
    },
    
    abrirEdicao(token) {
      // Em uma versão futura, implementar edição de metadados
      this.mostrarSnackbar(`Edição de ${token.symbol} será implementada em breve`, 'info')
    }
  },
  async mounted() {
    await this.carregarTokens()
  }
}
</script>

<style scoped>
.tokens-view {
  padding-bottom: 32px;
}
</style>
